import type { FC } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { AppBar, Box, IconButton, Toolbar, Link, Avatar } from '@mui/material';
import useAuth from 'src/hooks/useAuth';
import { experimentalStyled } from '@mui/material/styles';
import type { AppBarProps } from '@mui/material';
import MenuIcon from 'src/icons/Menu';
import LogoutIcon from 'src/icons/Logout';
import Logo from 'src/components/Logo';
import { PATH_DASHBOARD } from 'src/routes/paths';
import toast from 'react-hot-toast';

interface DashboardNavbarProps extends AppBarProps {
  onSidebarMobileOpen?: () => void;
}

const DashboardNavbarRoot = experimentalStyled(AppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer - 100,
  height: 82,
  background: `url(/static/islamic-pattern.png) ${theme.palette.primary.main}`,
}));

const DashboardNavbar: FC<DashboardNavbarProps> = (props) => {
  const { onSidebarMobileOpen, ...other } = props;
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error(err);
      toast.error('Unable to logout.');
    }
  };

  return (
    <DashboardNavbarRoot {...other}>
      <Toolbar sx={{ padding: '8px 16px' }}>
        <IconButton
          color="inherit"
          onClick={onSidebarMobileOpen}
          sx={{
            display: {
              lg: 'none',
            },
          }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>
        <Link
          component={RouterLink}
          to={PATH_DASHBOARD.overview}
          sx={{ height: '100%' }}
        >
          <Logo
            sx={{
              display: {
                lg: 'inline',
                xs: 'none',
              },
              height: '100%',
              width: 170,
            }}
          />
        </Link>
        <Box
          sx={{
            flexGrow: 1,
            ml: 2,
          }}
        />
        <Box sx={{ display: 'flex' }}>
          <LogoutIcon
            sx={{ width: 41, height: 41, cursor: 'pointer', ml: 1 }}
            onClick={handleLogout}
          />
          <Link component={RouterLink} to={PATH_DASHBOARD.profile}>
            <Avatar
              src="/static/user.svg"
              sx={{
                height: 41,
                width: 41,
                color: '#fff',
                ml: 1,
              }}
            />
          </Link>
        </Box>
      </Toolbar>
    </DashboardNavbarRoot>
  );
};

DashboardNavbar.propTypes = {
  onSidebarMobileOpen: PropTypes.func,
};

export default DashboardNavbar;
